import HMACClient from './hmacClient';

/**
 * Configuração da URL base da API baseada no ambiente
 */
const getAPIURL = () => {
  return process.env.REACT_APP_GOLANG_API || "http://192.168.50.136:8080/api/v2";
};

/**
 * Cliente HMAC configurado para a API da Paradigma
 * Usa a chave HMAC da variável de ambiente ou fallback para desenvolvimento
 */
const secureClient = new HMACClient(getAPIURL());

/**
 * Serviço de API Segura da Paradigma
 *
 * Fornece métodos específicos para endpoints que requerem autenticação HMAC
 */
class SecureApiService {

  /**
   * Cria uma nova assinatura/subscription
   * @param {Object} subscriptionData - Dados da assinatura
   * @param {string} subscriptionData.email - Email do usuário
   * @param {string} subscriptionData.plan - Plano escolhido
   * @param {Object} subscriptionData.paymentData - Dados de pagamento
   * @returns {Promise<Object>} Resposta da API com dados da assinatura criada
   */
  async createSubscription(subscriptionData) {
    try {
      const response = await secureClient.post('/subscriptions', subscriptionData);
      return response.data;
    } catch (error) {
      console.error('Erro ao criar assinatura:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Obtém informações de uma assinatura específica
   * @param {string} subscriptionId - ID da assinatura
   * @returns {Promise<Object>} Dados da assinatura
   */
  async getSubscription(subscriptionId) {
    try {
      const response = await secureClient.get(`/subscriptions/${subscriptionId}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar assinatura:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Lista assinaturas do usuário
   * @param {Object} filters - Filtros para a listagem
   * @param {string} filters.email - Email do usuário
   * @param {string} filters.status - Status da assinatura
   * @returns {Promise<Array>} Lista de assinaturas
   */
  async listSubscriptions(filters = {}) {
    try {
      const params = new URLSearchParams(filters);
      const url = `/subscriptions${params.toString() ? '?' + params.toString() : ''}`;
      const response = await secureClient.get(url);
      return response.data;
    } catch (error) {
      console.error('Erro ao listar assinaturas:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Atualiza uma assinatura existente
   * @param {string} subscriptionId - ID da assinatura
   * @param {Object} updateData - Dados para atualização
   * @returns {Promise<Object>} Dados da assinatura atualizada
   */
  async updateSubscription(subscriptionId, updateData) {
    try {
      const response = await secureClient.put(`/subscriptions/${subscriptionId}`, updateData);
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar assinatura:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Cancela uma assinatura
   * @param {string} email - Email do usuário para cancelar assinatura
   * @returns {Promise<Object>} Confirmação do cancelamento
   */
  async cancelSubscription(email) {
    try {
      const response = await secureClient.delete('/subscriptions', {
        data: { email }
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao cancelar assinatura:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Pausa uma assinatura
   * @param {string} subscriptionId - ID da assinatura
   * @param {Object} pauseData - Dados da pausa (duração, motivo, etc.)
   * @returns {Promise<Object>} Confirmação da pausa
   */
  async pauseSubscription(subscriptionId, pauseData) {
    try {
      const response = await secureClient.patch(`/subscriptions/${subscriptionId}/pause`, pauseData);
      return response.data;
    } catch (error) {
      console.error('Erro ao pausar assinatura:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Reativa uma assinatura pausada
   * @param {string} subscriptionId - ID da assinatura
   * @returns {Promise<Object>} Confirmação da reativação
   */
  async resumeSubscription(subscriptionId) {
    try {
      const response = await secureClient.patch(`/subscriptions/${subscriptionId}/resume`);
      return response.data;
    } catch (error) {
      console.error('Erro ao reativar assinatura:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Obtém histórico de pagamentos de uma assinatura
   * @param {string} subscriptionId - ID da assinatura
   * @returns {Promise<Array>} Lista de pagamentos
   */
  async getSubscriptionPayments(subscriptionId) {
    try {
      const response = await secureClient.get(`/subscriptions/${subscriptionId}/payments`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar pagamentos:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Valida dados de uma assinatura antes de criar
   * @param {Object} subscriptionData - Dados para validação
   * @returns {Promise<Object>} Resultado da validação
   */
  async validateSubscriptionData(subscriptionData) {
    try {
      const response = await secureClient.post('/subscriptions/validate', subscriptionData);
      return response.data;
    } catch (error) {
      console.error('Erro ao validar dados:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Processa upgrade/downgrade de plano
   * @param {string} subscriptionId - ID da assinatura
   * @param {Object} planChangeData - Dados da mudança de plano
   * @param {string} planChangeData.newPlan - Novo plano
   * @param {string} planChangeData.changeType - Tipo de mudança (upgrade/downgrade)
   * @returns {Promise<Object>} Dados da assinatura atualizada
   */
  async changePlan(subscriptionId, planChangeData) {
    try {
      const response = await secureClient.patch(`/subscriptions/${subscriptionId}/plan`, planChangeData);
      return response.data;
    } catch (error) {
      console.error('Erro ao alterar plano:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Trata erros da API e retorna objeto padronizado
   * @param {Error} error - Erro capturado
   * @returns {Object} Objeto de erro padronizado
   */
  handleError(error) {
    if (error.response) {
      // Erro da resposta HTTP - preservar error.response para parseIuguError funcionar
      const customError = new Error(error.response.data?.message || 'Erro na API');
      customError.type = 'API_ERROR';
      customError.status = error.response.status;
      customError.response = error.response; // Preservar response original
      customError.details = error.response.data;
      return customError;
    } else if (error.request) {
      // Erro de rede
      const customError = new Error('Erro de conexão com o servidor');
      customError.type = 'NETWORK_ERROR';
      customError.details = error.request;
      return customError;
    } else {
      // Erro de configuração ou outro
      const customError = new Error(error.message || 'Erro desconhecido');
      customError.type = 'UNKNOWN_ERROR';
      customError.details = error;
      return customError;
    }
  }

  /**
   * Método genérico para requisições customizadas
   * Útil para endpoints específicos que não estão mapeados
   * @param {string} method - Método HTTP (GET, POST, PUT, DELETE, etc.)
   * @param {string} url - URL do endpoint
   * @param {Object} data - Dados da requisição
   * @param {Object} headers - Headers adicionais
   * @returns {Promise<Object>} Resposta da API
   */
  async customRequest(method, url, data = null, headers = {}) {
    try {
      const config = {
        method,
        url,
        headers,
        ...(data && { data })
      };
      const response = await secureClient.request(config);
      return response.data;
    } catch (error) {
      console.error('Erro em requisição customizada:', error);
      throw this.handleError(error);
    }
  }
}

// Exportar instância singleton do serviço
const secureApiService = new SecureApiService();

export default secureApiService;

// Exportar também a classe para casos onde múltiplas instâncias sejam necessárias
export { SecureApiService, secureClient };